/**
 * The units the org's numbers are SHOWN in (next-phase plan, step 7; migration 0202). Storage never changes: an
 * import leaves each value in a unit with the unit beside it, and an org admin chooses what the screen shows.
 *
 *   GET /v1/twin/settings/units   {prefs, source, resolved}: any member reads. `source` is "default" (metric, no
 *                                 overrides) until an org admin chooses, then "org"
 *   PUT /v1/twin/settings/units   the choice {system, overrides}, or null to clear it (org admin)
 *     400 invalid_units           the detail says which part of the choice is not one
 *     403 insufficient_permission only an org admin chooses
 *
 * One cached read (DISPLAY_UNITS_KEY) serves every page that shows a number; `useDisplayUnits` is metric until it
 * answers, and where the live platform does not serve the route, so a page never waits on it to show a value. The
 * conversion is the web's own (lib/units), held to the server's by the shared vectors.
 */
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, ApiClientError } from "@/lib/api-client";
import { formatQuantity, type FormatOptions, type Formatted } from "@/lib/units/format";
import { DEFAULT_SI, prefsOf, resolvedOf, unitFor, type DimensionKey, type DisplayPrefs } from "@/lib/units/prefs";
import { ACTIONS_KEY } from "./actions";
import { BRIEF_EMAIL_KEY, BRIEF_NARRATIVE_KEY, CADENCE_BRIEF_KEY, PLANT_BRIEF_KEY, useBriefLive } from "./brief";

const SETTINGS = "/v1/twin/settings/units";

/** Where the choice comes from: nobody has chosen (metric), or an org admin has. */
export type DisplayUnitsSource = "default" | "org";

export interface DisplayUnitsAnswer {
  prefs: DisplayPrefs;
  source: DisplayUnitsSource;
  /** What each dimension is shown in, keyed by its canonical unit. */
  resolved: Record<DimensionKey, string>;
}

/** The cache key every reader of the units shares. */
export const DISPLAY_UNITS_KEY = ["display-units"] as const;

/**
 * The server's answer, read defensively: anything that is not a choice is the metric default. `resolved` is worked out
 * from the choice with the web's own table (the server's `resolved` is its twin, and the vectors hold the two tables
 * together), so a page never labels a unit the conversion cannot make.
 */
export function answerOf(raw: unknown): DisplayUnitsAnswer {
  const body = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  const prefs = prefsOf(body.prefs);
  // The org's own only where the body says so AND its choice reads as one; anything else is the default.
  const chosen = body.source === "org" && isChoice(body.prefs);
  return { prefs, source: chosen ? "org" : "default", resolved: resolvedOf(prefs) };
}

function isChoice(raw: unknown): boolean {
  const system = typeof raw === "object" && raw !== null ? (raw as { system?: unknown }).system : undefined;
  return system === "si" || system === "us";
}

/** The org's choice. A platform that does not serve the route (404) reads as the default. */
export async function getDisplayUnits(): Promise<DisplayUnitsAnswer> {
  try {
    return answerOf(await api.get<unknown>(SETTINGS));
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return answerOf(null);
    throw err;
  }
}

/** Sets the org's choice (org admins), or clears it with null. */
export async function putDisplayUnits(prefs: DisplayPrefs | null): Promise<DisplayUnitsAnswer> {
  return answerOf(await api.put<unknown>(SETTINGS, prefs));
}

/** The org's units, read on the live platform only; kept five minutes. */
export function useDisplayUnitsQuery() {
  const live = useBriefLive();
  return useQuery({ queryKey: DISPLAY_UNITS_KEY, queryFn: getDisplayUnits, enabled: live, staleTime: 5 * 60_000 });
}

export interface DisplayUnitsState {
  /** The choice; metric until the read answers. */
  prefs: DisplayPrefs;
  source: DisplayUnitsSource;
  /**
   * The choice a value is shown in: the org's own, or undefined until the read answers and for an org that has not
   * chosen, so every value then shows as stored, as the server serves it (#1190 review MEDIUM 4). A page converts by
   * this, never by `prefs`, which is the settings form's.
   */
  shown: DisplayPrefs | undefined;
  resolved: Record<DimensionKey, string>;
  /** The read is on its way (so the form waits, while a value shown meanwhile is metric). */
  isLoading: boolean;
  /** The read failed (a value is shown in metric). */
  isError: boolean;
  /** The unit a value stored in `stored` is shown in. */
  unitFor: (stored: string) => string;
  /** A stored quantity as the org reads it. */
  format: (value: number, storedUnit: string, opts?: FormatOptions) => Formatted;
}

/** The org's units, with the conversion bound to them. Metric before the read answers, so no page waits on it. */
export function useDisplayUnits(): DisplayUnitsState {
  const q = useDisplayUnitsQuery();
  const answer = q.data;
  const isLoading = q.isLoading;
  const isError = q.isError;
  return useMemo(() => {
    const prefs = answer?.prefs ?? DEFAULT_SI;
    return {
      prefs,
      source: answer?.source ?? "default",
      shown: answer?.source === "org" ? prefs : undefined,
      resolved: answer?.resolved ?? resolvedOf(DEFAULT_SI),
      isLoading,
      isError,
      unitFor: (stored: string) => unitFor(stored, prefs),
      format: (value: number, storedUnit: string, opts?: FormatOptions) => formatQuantity(value, storedUnit, prefs, opts),
    };
  }, [answer, isLoading, isError]);
}

/**
 * A stored quantity as the org reads it, for a component to call: `format(value, storedUnit)` is
 * `{text, value, unit}` (formatQuantity with the org's choice). Metric before the read answers. A page that formats
 * its own way and only needs the conversion reads `useDisplayUnits().prefs` and `shownFor` (lib/units/display).
 */
export function useUnitFormat(): DisplayUnitsState["format"] {
  return useDisplayUnits().format;
}

/**
 * After a save: keeps the answer for every reader, and reads again what the server serves in the org's units (the
 * briefs, their summaries, the action list and the email's preview), so nothing cached shows the old units (#1190
 * review MEDIUM 7).
 */
export function afterUnitsSaved(qc: QueryClient, answer: DisplayUnitsAnswer): void {
  qc.setQueryData(DISPLAY_UNITS_KEY, answer);
  for (const key of [PLANT_BRIEF_KEY, CADENCE_BRIEF_KEY, BRIEF_NARRATIVE_KEY, BRIEF_EMAIL_KEY, ACTIONS_KEY]) {
    void qc.invalidateQueries({ queryKey: key });
  }
}

/** Saves the org's choice (org admins), or null to clear it. */
export function useSaveDisplayUnits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: putDisplayUnits,
    onSuccess: (answer) => afterUnitsSaved(qc, answer),
  });
}
